import { useTranslation } from 'react-i18next';
import { RiFileGifLine, RiImageLine, RiPlayFill, RiPlayLine } from '@remixicon/react';
import { useState } from 'react';
import type { ElementOf } from '@atnt/shared';
import { cn } from '@/shared/lib/cn';
import { DataIcon } from '../DataIcon';
import { useServerNow } from '../live/clock';
import { formatClock, formatDate, formatDateTime, NO_VALUE, withUnit } from '../live/format';
import { useValueSource, useLocale } from '../live/values';
import { STATUS_STYLE } from '../live/status';
import { dp } from '../units';
import { useScene } from './scene';

/**
 * Unpositioned element bodies (the positioned wrapper is `ElementView`). Every body renders two looks:
 * `viewer` (14:7285 glass + Montserrat) and `canvas` (317:6923 constructor widgets, Inter).
 */

/* ───────────────────────── Sarlavha (title) ───────────────────────── */

export function TitleBody({ element, showLogo = false }: { element: ElementOf<'title'>; showLogo?: boolean }) {
  const { variant, mode, brandLogoUrl } = useScene();
  const { t } = useTranslation('constructor');
  const editing = mode === 'edit';
  const title = element.title || (editing ? t('elements.titlePlaceholder') : '');
  const subtitle = element.subtitle || (editing && !element.title ? t('elements.subtitlePlaceholder') : '');

  if (variant === 'viewer') {
    // 14:7285 logo block: logo 113×62 · gap 24.5 · bar 6×62 (#16a851) · gap 24.5 · Montserrat SemiBold uppercase #0085ff
    const logo = showLogo ? brandLogoUrl : null;
    return (
      <div className="flex items-center gap-24 font-display">
        {logo && <img src={logo} alt="" draggable={false} className="h-62 w-113 shrink-0 object-contain object-left" />}
        {element.showAccentBar && <span aria-hidden className="w-6 shrink-0 self-stretch bg-other-16a851" style={{ minHeight: dp(62) }} />}
        <div className="flex min-w-0 flex-col justify-center gap-4">
          {title && (
            <p className="leading-[1.2] font-semibold whitespace-pre-line text-other-0085ff uppercase" style={{ fontSize: dp(element.titleSize), maxWidth: dp(960) }}>
              {title}
            </p>
          )}
          {subtitle && (
            <p className="leading-[1.3] font-medium text-white-800" style={{ fontSize: dp(element.subtitleSize), maxWidth: dp(960) }}>
              {subtitle}
            </p>
          )}
        </div>
      </div>
    );
  }

  // 317:7032: padding 12/14, gap 14, bar 5 Blue/1000; title Bold 24/32 −1%; subtitle Medium 14/20 Blue/1000
  return (
    <div className="flex gap-14 px-14 py-12">
      {element.showAccentBar && <span aria-hidden className="w-5 shrink-0 rounded-full bg-blue-1000" />}
      <div className="flex min-w-0 flex-col gap-4">
        <p
          className={cn('font-bold tracking-figma whitespace-pre-line', element.title ? 'text-white' : 'text-white-500')}
          style={{ fontSize: dp(element.titleSize), lineHeight: dp(Math.round((element.titleSize * 4) / 3)), maxWidth: dp(960) }}
        >
          {title}
        </p>
        {subtitle && (
          <p
            className={cn('font-medium', element.subtitle ? 'text-blue-1000' : 'text-blue-600')}
            style={{ fontSize: dp(element.subtitleSize), lineHeight: dp(Math.round(element.subtitleSize * 1.43)), maxWidth: dp(960) }}
          >
            {subtitle}
          </p>
        )}
      </div>
    </div>
  );
}

/* ───────────────────────── Soat / Sana ───────────────────────── */

export function ClockBody({ element }: { element: ElementOf<'clock'> }) {
  const { variant, timeZone } = useScene();
  const now = useServerNow(element.showSeconds ? 1000 : 60_000);
  const text = formatClock(now, { seconds: element.showSeconds, timeZone });
  if (variant === 'viewer') {
    // 14:7285: 100×44 glass pill r8, Montserrat Bold 20
    return (
      <time className="glass-viewer-pill flex h-44 min-w-100 items-center justify-center rounded-8 px-19 font-display text-xl leading-none font-bold text-white tabular-nums">
        {text}
      </time>
    );
  }
  return <time className="block rounded-16 border border-white-100 bg-white-50 px-14 py-12 text-xl font-extrabold text-white tabular-nums">{text}</time>;
}

export function DateBody({ element }: { element: ElementOf<'date'> }) {
  const { variant, timeZone } = useScene();
  const locale = useLocale();
  const now = useServerNow(60_000);
  const text = formatDate(now, element.format, { locale, timeZone });
  if (variant === 'viewer') {
    // 14:7285: 147×44 glass pill r8, Montserrat Bold 20
    return (
      <time className="glass-viewer-pill flex h-44 items-center justify-center rounded-8 px-12 font-display text-xl leading-none font-bold whitespace-nowrap text-white tabular-nums">
        {text}
      </time>
    );
  }
  return <time className="block rounded-16 border border-white-100 bg-white-50 px-14 py-12 text-xl font-extrabold whitespace-nowrap text-white">{text}</time>;
}

/* ───────────────────────── Ko'rsatkich (indicator / KPI) ───────────────────────── */

export function IndicatorBody({ element }: { element: ElementOf<'indicator'> }) {
  const { variant } = useScene();
  const { t } = useTranslation('constructor');
  const { timeZone } = useScene();
  const value = useValueSource(element.source, element.unit || undefined);
  // manual values carry their unit in the element ("24" + "°C", 359:5729 QIYMAT · BIRLIK)
  const manualUnit = element.source.kind === 'manual' && value.text !== NO_VALUE ? element.unit.trim() : '';
  const unit = value.unit || manualUnit;
  const text = manualUnit ? withUnit(value.text, manualUnit) : value.text;
  const label = element.label || (element.source.kind === 'sensor' ? element.source.binding.snapshot.name : '');
  const status = value.status;
  const stale = value.reading?.stale ?? false;
  const offline = status === 'offline' || stale;
  const tone = status === 'warning' || status === 'danger' ? STATUS_STYLE[status] : null;
  const tooltip = value.reading?.ts ? t('live.lastUpdate', { time: formatDateTime(value.reading.ts, { timeZone }) }) : undefined;

  if (variant === 'viewer') {
    // 14:7285: 308×90 glass r20 (blur 16); icon 44 at +30; value Montserrat Bold 24, label SemiBold 14 #05b4ff
    return (
      <div title={tooltip} className="glass-viewer flex h-90 w-308 items-center gap-20 overflow-hidden rounded-20 pr-16 pl-30">
        <span
          className="flex size-44 shrink-0 items-center justify-center rounded-12"
          style={{ color: tone ? tone.dot : '#05b4ff', backgroundColor: tone ? tone.halo : undefined }}
        >
          <DataIcon name={element.icon} className="size-36" />
        </span>
        <div className="flex min-w-0 flex-col">
          <p className={cn('truncate text-2xl leading-[1.2] font-bold', offline ? 'text-white-400' : 'text-white')}>{text}</p>
          <p className="truncate text-sm leading-[1.2] font-semibold text-other-05b4ff">{label}</p>
        </div>
      </div>
    );
  }

  // 317:7043: HUG, padding 12/16, gap 12, White/100; icon box 40 r10; value Bold 20 + unit Medium 13 White/600; label Medium 12
  return (
    <div title={tooltip} className="flex items-center gap-12 rounded-16 border border-white-100 bg-white-100 px-16 py-12">
      <span
        className="flex size-40 shrink-0 items-center justify-center rounded-10"
        style={{ backgroundColor: tone ? tone.halo : '#ffffff1a', color: tone ? tone.dot : '#ffffff' }}
      >
        <DataIcon name={element.icon} className="size-20" />
      </span>
      <div className="flex min-w-0 flex-col gap-2">
        <p className="flex items-baseline gap-4 whitespace-nowrap">
          <span className={cn('text-xl leading-auto font-bold tracking-normal', offline ? 'text-white-400' : 'text-white')}>{value.number}</span>
          {unit && <span className="text-13 leading-auto font-medium text-white-600">{unit}</span>}
        </p>
        {label && <p className="truncate text-xs leading-auto font-medium tracking-normal text-white-600">{label}</p>}
      </div>
    </div>
  );
}

/* ───────────────────────── Rasm / GIF / Video ───────────────────────── */

const ASPECT: Record<ElementOf<'image'>['aspect'], number> = { '16:9': 16 / 9, '4:3': 4 / 3, '1:1': 1 };

export const mediaSize = (el: Pick<ElementOf<'image'>, 'width' | 'aspect'>) => ({ w: el.width, h: el.width / ASPECT[el.aspect] });

export function MediaBody({ element }: { element: ElementOf<'image'> | ElementOf<'gif'> | ElementOf<'video'> }) {
  const { variant, mode, fileUrl } = useScene();
  const { t } = useTranslation('constructor');
  const { w, h } = mediaSize(element);
  const media = element.media;
  const frame = { width: dp(w), height: dp(h) };

  if (!media) {
    if (variant === 'viewer' && mode !== 'edit') return null; // nothing to show to viewers
    const Icon = element.type === 'video' ? RiPlayLine : element.type === 'gif' ? RiFileGifLine : RiImageLine;
    return (
      <div style={frame} className="flex flex-col items-center justify-center gap-8 rounded-12 border border-white-100 bg-primary-bg-1000 font-sans">
        <Icon className="size-26 text-white-600" aria-hidden />
        <span className="text-xs leading-auto font-medium tracking-normal text-white-600">{t(`elements.${element.type}Placeholder`)}</span>
      </div>
    );
  }

  const zoom = { transform: element.zoom !== 1 ? `scale(${element.zoom})` : undefined };
  return (
    <div style={frame} className="relative overflow-hidden rounded-12 bg-black">
      {element.type === 'video' ? (
        <VideoMedia src={fileUrl(media.fileId)} element={element} interactive={mode !== 'edit'} style={zoom} />
      ) : (
        <img src={fileUrl(media.fileId)} alt={element.name} draggable={false} className="size-full object-cover" style={zoom} />
      )}
    </div>
  );
}

function VideoMedia({ src, element, interactive, style }: { src: string; element: ElementOf<'video'>; interactive: boolean; style: { transform?: string } }) {
  const { t } = useTranslation('constructor');
  const [started, setStarted] = useState(false);
  const autoplay = element.autoplayMuted && interactive;
  return (
    <>
      <video
        src={src}
        className="size-full object-cover"
        style={style}
        autoPlay={autoplay}
        muted={element.autoplayMuted || !started}
        loop={element.loop}
        playsInline
        preload="metadata"
        controls={interactive && !element.autoplayMuted && started}
        ref={(v) => {
          if (v && started && v.paused) void v.play().catch(() => undefined);
        }}
      >
        <track kind="captions" />
      </video>
      {interactive && !element.autoplayMuted && !started && (
        <button
          type="button"
          onClick={() => setStarted(true)}
          aria-label={t('elements.play')}
          className="absolute inset-0 m-auto flex size-56 items-center justify-center rounded-full bg-black-600 text-white hover:bg-black-700"
        >
          <RiPlayFill className="size-28" aria-hidden />
        </button>
      )}
    </>
  );
}
